<script setup lang="ts">
import { onBeforeMount, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import UIKit from '@pocketprep/ui-kit'
import Skeleton from '@/components/Skeleton.vue'
import { userModule } from '@/store/user/module'

const {
    ModalContainer,
    Button: PocketButton,
    Icon,
} = UIKit

const route = useRoute()
const router = useRouter()

const email = ref('')
const code = ref('')
const errorMessage = ref('')
const isLoading = ref(false)
const isResending = ref(false)
const highlightInput = ref(false)
const cursorIndex = ref(0)

const goToSignIn = () => {
    router.push({ name: 'sign-in' })
}

const setHighlightInput = (value: boolean) => {
    highlightInput.value = value
}

const submitSignIn = async () => {
    isLoading.value = true
    errorMessage.value = ''

    if (email.value && code.value) {
        if (code.value.trim().length !== 6) {
            errorMessage.value = 'Code must be six digits'
        } else {
            try {
                const user = await userModule.actions.signInWithCode({
                    email: email.value,
                    code: code.value,
                })
                if (!user.currentOrganization) {
                    router.push({ name: 'register' })
                } else if (route.query.redirect && typeof route.query.redirect === 'string') {
                    router.push({ path: route.query.redirect })
                } else {
                    router.push({ name: 'stats' })
                }
            } catch (err) {
                errorMessage.value = 'That code wasn\'t right or it expired'
            }
        }
    }

    isLoading.value = false
}

const resendEmail = async () => {
    errorMessage.value = ''
    isResending.value = true
    try {
        await userModule.actions.sendMagicEmail({ email: email.value })
        router.push({
            name: 'email-auth',
            query: {
                email: email.value.toLowerCase(),
                redirect: route.query.redirect,
            },
        })
    } catch (err) {
        errorMessage.value = 'We weren\'t able to send the link'
    }
    isResending.value = false
}

const handleKeyDown = (event: KeyboardEvent) => {
    // Allow tab, refresh, and paste events to pass through, otherwise prevent default keydown behavior
    if (
        event.key === 'Tab'
        || (event.key === 'r' && event.metaKey)
        || (event.key === 'v' && event.metaKey)
    ) {
        return
    } else {
        event.preventDefault()
    }

    if (event.key.match(/[0-9]/)) {
        code.value = code.value.slice(0, cursorIndex.value) + event.key + code.value.slice(cursorIndex.value + 1)
        cursorIndex.value = Math.min(cursorIndex.value + 1, 5)
    } else if (event.key === 'ArrowLeft') {
        cursorIndex.value = Math.max(cursorIndex.value - 1, 0)
    } else if (event.key === 'ArrowRight') {
        cursorIndex.value = Math.min(cursorIndex.value + 1, 5)
    } else if (event.key === 'Backspace') {
        if (code.value[cursorIndex.value]) {
            code.value = (code.value.slice(0, cursorIndex.value) + ' ' + code.value.slice(cursorIndex.value + 1))
                .trimEnd()
        }
        cursorIndex.value = Math.min(Math.max(cursorIndex.value - 1, 0), code.value.length)
    } else if (event.key === 'Enter') {
        submitSignIn()
    }
}

const handleClick = (event: MouseEvent) => {
    const selectionEnd = (event.target as HTMLInputElement)?.selectionEnd
    if (selectionEnd !== null) {
        cursorIndex.value = selectionEnd
    }
}

const handlePaste = (event: ClipboardEvent) => {
    errorMessage.value = ''
    const pasteValue = event.clipboardData?.getData('text')
    if (pasteValue && typeof Number(pasteValue) === 'number' && pasteValue.length === 6) {
        code.value = ''
        cursorIndex.value = Math.min(pasteValue.length, 5)
    } else if (pasteValue) {
        errorMessage.value = `Invalid code: ${pasteValue}. Code must be six digits`
        event.preventDefault()
    }
}

onBeforeMount(() => {
    if (route.query) {
        if (typeof route.query.code === 'string') {
            const queryCode = route.query.code
            if (typeof Number(queryCode) === 'number' && queryCode.length === 6) {
                code.value = queryCode
            } else {
                errorMessage.value = `Invalid code: ${queryCode}. Code must be six digits`
            }
        }
        if (typeof route.query.email === 'string') {
            email.value = route.query.email

            if (code.value) {
                submitSignIn()
            }
        } else {
            router.push({
                name: 'sign-in',
            })
        }
    }
})
</script>

<template>
    <Skeleton class="email-auth">
        <Portal to="modal">
            <ModalContainer key="emailAuthModal">
                <div
                    class="email-auth__form"
                >
                    <div
                        class="email-auth__back-btn"
                        @click="goToSignIn"
                    >
                        <Icon
                            class="email-auth__back-arrow-icon"
                            type="arrow"
                        />
                        Back
                    </div>
                    <div v-if="!isResending && !errorMessage" class="email-auth__link-sent">
                        <Icon
                            class="email-auth__link-sent-icon"
                            type="check"
                        />
                        Link Sent
                    </div>
                    <div class="email-auth__text-big">
                        Check your email
                    </div>
                    <div class="email-auth__text-small">
                        Tap the button in the email we sent to {{ email }} or enter code from email below.
                    </div>
                    <div class="email-auth__code-input-container">
                        <div
                            v-for="n in 6"
                            :key="n"
                            class="email-auth__code-input-box"
                            :class="{
                                'email-auth__code-input-box--active': highlightInput && n === cursorIndex + 1
                            }"
                        />
                        <input
                            v-model="code"
                            class="email-auth__code-input"
                            type="text"
                            maxlength="6"
                            :autofocus="code.length !== 6"
                            @focus="setHighlightInput(true)"
                            @blur="setHighlightInput(false)"
                            @keydown="handleKeyDown"
                            @click="handleClick"
                            @paste="handlePaste"
                        >
                    </div>
                    <div v-if="errorMessage" class="email-auth__error-message">
                        <Icon class="email-auth__error-message-icon" type="warning" />
                        <span>{{ errorMessage }}</span>
                    </div>
                    <PocketButton
                        class="email-auth__email-auth-btn"
                        :disabled="isLoading"
                        :autofocus="code.length === 6"
                        @click="submitSignIn"
                    >
                        Sign In
                    </PocketButton>
                    <PocketButton
                        type="tertiary"
                        class="email-auth__resend-email-btn"
                        :disabled="isResending"
                        @click="resendEmail"
                    >
                        Resend Email
                    </PocketButton>
                </div>
            </ModalContainer>
        </Portal>
    </Skeleton>
</template>

<style lang="scss" scoped>


.email-auth {
    &__form {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        width: 660px;
        height: 500px;
        background-color: $floral-white;
        background-image: url('@/assets/images/magic-link_background-image.svg');
        background-repeat: no-repeat;
        background-position: center;
    }

    &__back-btn {
        display: flex;
        align-items: center;
        position: absolute;
        top: 19px;
        left: 17px;
        color: $brand-blue;
        font-weight: 600;
        font-size: 14px;
        line-height: 19px;
        cursor: pointer;

        &:hover {
            color: $brand-black;
        }
    }

    &__back-arrow-icon {
        transform: rotate(180deg);
        width: 19px;
        height: 19px;
        margin-right: 10px;
    }

    &__link-sent {
        display: flex;
        align-items: center;
        position: absolute;
        top: 19px;
        font-size: 14px;
        line-height: 19px;
        color: $slate-03;
    }

    &__link-sent-icon {
        width: 14px;
        height: 14px;
        margin-right: 8px;
        margin-top: -2px;
    }

    &__text-big {
        font-size: 36px;
        line-height: 46px;
        font-weight: 600;
        margin-bottom: 13px;
    }

    &__text-small {
        font-size: 16px;
        line-height: 23px;
        width: 453px;
        text-align: center;
        margin-bottom: 40px;
    }

    &__code-input-container {
        position: relative;
        display: flex;
        margin-bottom: 28px;
        padding-left: 10px;
    }

    &__code-input-box {
        position: relative;
        background-color: white;
        border: 1px solid rgba($pewter, 0.7);
        border-radius: 4px;
        width: 42px;
        height: 50px;
        margin-right: 10px;
        box-sizing: border-box;

        &--active {
            border-width: 2px;
            border-color: $brand-blue;
        }

        &::after {
            content: '';
            position: absolute;
            top: 0;
            right: -11px;
            width: 9px;
            height: 100%;
            z-index: 2;
        }

        &:nth-child(6)::after {
            width: 100px;
            right: -102px;
        }
    }

    &__code-input {
        position: absolute;
        font-family: monospace;
        height: 50px;
        font-size: 28px;
        line-height: 38px;
        font-weight: 600;
        letter-spacing: 35.5px;
        padding-left: 12px;
        background: none;
        border: none;
        outline: none;
        caret-color: white;

        &::selection {
            background: none;
        }
    }

    &__error-message {
        display: flex;
        align-items: center;
        color: #c238f3;
        font-size: 14px;
        line-height: 19px;
        margin-top: -17px;
        margin-bottom: 26px;
    }

    &__error-message-icon {
        margin-right: 10px;
    }

    &__resend-email-btn {
        position: absolute;
        bottom: 24px;
    }
}
</style>
